// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import loadCustomerPartNumbers from "@steiner/App/Handlers/CustomerPartNumber/LoadCustomerPartNumbers";
import {
    DeleteCustomerPartNumberApiParameter,
    deleteCustomerPartNumberService,
} from "@steiner/App/Services/CustomerPartNumbersServices";
import { CustomAppState } from "@steiner/App/Store/Reducers";

type HandlerType = ApiHandler<
    DeleteCustomerPartNumberApiParameter & HasOnSuccess & HasOnError<string>,
    ServiceResult<any> & {
        errorMessage?: string;
    }
>;

export const DispatchBeginDeletingCustomerPartNumber: HandlerType = props => {
    props.dispatch({
        type: "xCustomerPartNumbers/BeginLoadingCustomerPartNumbers",
        isLoading: true,
    } as any);
};

export const RequestDataFromApi: HandlerType = async props => {
    const deleteCustomerResult = await deleteCustomerPartNumberService({
        customerPartNumber: props.parameter.customerPartNumber,
        productNumber: props.parameter.productNumber,
    });
    props.apiResult = deleteCustomerResult as any;
};

export const DispatchDeleteCustomerPartNumber: HandlerType = props => {
    if (props.apiResult.successful) {
        props.dispatch({
            type: "xCustomerPartNumbers/DeleteCustomerPartNumber",
            customerPartNumber: props.parameter.customerPartNumber,
            productNumber: props.parameter.productNumber,
        } as any);
    }
};

export const DispatchReloadCustomerProducts: HandlerType = props => {
    if (props.apiResult.successful) {
        const pagination = (props.getState() as CustomAppState).xCustomerPartNumbers.pagination;
        props.dispatch(
            loadCustomerPartNumbers({
                pageSize: pagination ? pagination.pageSize : 8,
                page: pagination ? pagination.page : 1,
            }),
        );
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful && props.parameter.onSuccess) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess();
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const chain = [
    DispatchBeginDeletingCustomerPartNumber,
    RequestDataFromApi,
    DispatchDeleteCustomerPartNumber,
    DispatchReloadCustomerProducts,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const deleteCustomerPartNumber = createHandlerChainRunner(chain, "deleteCustomerPartNumber");

export default deleteCustomerPartNumber;
